<script setup lang="ts">
/**
 * Page DÉTAIL d'un collaborateur (V2) — inventaire consolidé du matériel détenu.
 *
 * Montée sur `/onboarding/collaborateurs/:userUuid`. Consomme le store onboarding :
 * carte identité, remplacements en cours (OnbReplacementTracker), matériel détenu
 * regroupé par catégorie avec action « Demander un remplacement »
 * (OnbReplacementDialog), et objets en cours de dotation (lecture seule).
 * Ton visuel aligné sur OnboardingDetailPage.vue (breadcrumb, cards, badges).
 */
import { computed, nextTick, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import {
  ArrowPathIcon,
  CalendarDaysIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  InboxArrowDownIcon,
  PencilSquareIcon,
  PlusIcon,
  ShoppingCartIcon,
  SparklesIcon,
  UserIcon,
} from '@heroicons/vue/24/outline'
import { useOnboardingStore } from '@/v2/stores/useOnboardingStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import {
  CATEGORY_META,
  CONDITION_LABEL,
  attributeLabel,
  isReplacementActive,
  isSensitiveAttribute,
} from '@/v2/onboarding/catalog'
import type { AssetAssignment, AssetCategory, OwnedAsset } from '@/v2/onboarding/types'
import OnbAssetStatusBadge from '@/v2/components/onboarding/OnbAssetStatusBadge.vue'
import OnbAssetEditDialog from '@/v2/components/onboarding/OnbAssetEditDialog.vue'
import OnbSecretValue from '@/v2/components/onboarding/OnbSecretValue.vue'
import OnbReplacementDialog from '@/v2/components/onboarding/OnbReplacementDialog.vue'
import OnbReplacementTracker from '@/v2/components/onboarding/OnbReplacementTracker.vue'
import OnbAssignAssetDialog from '@/v2/components/onboarding/OnbAssignAssetDialog.vue'

const route = useRoute()
const store = useOnboardingStore()
const perms = useBoostCorePermissionStore()

/** L'utilisateur peut-il administrer le matériel (modifier un équipement) ? */
const canManage = computed(() => perms.can('MATERIAL_WRITE'))

const userUuid = route.params.userUuid as string
const collab = computed(() => store.getCollaboratorByUuid(userUuid))

function fullName(): string {
  const c = collab.value
  return c ? `${c.firstName} ${c.lastName}`.trim() : ''
}

// ── Identité ──────────────────────────────────────────────────────────────────
const identityFields = computed(() => {
  const c = collab.value
  if (!c) return []
  return [
    { label: 'Poste', value: c.jobTitle || '—' },
    { label: 'Entité', value: c.entity || '—' },
    { label: 'Manager (N+1)', value: c.manager || '—' },
    { label: 'Agence', value: c.agency || '—' },
    { label: 'Email', value: c.email || '—' },
    { label: 'Mobile', value: c.mobile || '—', numeric: true },
    { label: '3CX', value: c.phone3cx || '—', numeric: true },
  ]
})

// ── Remplacements ─────────────────────────────────────────────────────────────
const activeTickets = computed(
  () => collab.value?.tickets.filter((t) => isReplacementActive(t.status)) ?? [],
)
const closedTickets = computed(
  () => collab.value?.tickets.filter((t) => !isReplacementActive(t.status)) ?? [],
)

/** Un ticket actif existe-t-il déjà pour cet objet (l'ancien d'un remplacement) ? */
function hasActiveReplacement(assetId: string): boolean {
  return (
    collab.value?.tickets.some(
      (t) => t.oldAssignmentId === assetId && isReplacementActive(t.status),
    ) ?? false
  )
}

// ── Matériel détenu, regroupé par catégorie ───────────────────────────────────
const CATEGORY_ORDER: AssetCategory[] = ['IT', 'TELECOM', 'VEHICLE', 'ACCESS', 'OTHER']

const ownedGroups = computed(() => {
  const owned = collab.value?.ownedAssets ?? []
  return CATEGORY_ORDER.map((category) => ({
    category,
    label: CATEGORY_META[category].label,
    assets: owned.filter((a) => a.category === category),
  })).filter((g) => g.assets.length > 0)
})

const hasOwned = computed(() => (collab.value?.ownedAssets.length ?? 0) > 0)

/**
 * Attributs « clés » d'un objet (SN, IMEI, ligne, plaque…) : tout ce qui est
 * renseigné et non sensible. L'inventaire étant peuplé selon le type, cela
 * affiche naturellement les infos pertinentes pour chaque matériel.
 */
function keyAttributes(asset: OwnedAsset): { key: string; value: string }[] {
  return Object.entries(asset.attributes)
    .filter(([key, value]) => value && !isSensitiveAttribute(key))
    .map(([key, value]) => ({ key, value }))
}

/** Valeur d'inventaire ressemblant à un numéro (→ tabular-nums + break-all). */
function looksNumeric(value: string): boolean {
  return /^[\d\s/.\-+]+$/.test(value.trim())
}

/** Attributs sensibles d'un objet (masqués par défaut via OnbSecretValue). */
function sensitiveAttributes(asset: OwnedAsset): { key: string; value: string }[] {
  return Object.entries(asset.attributes)
    .filter(([key, value]) => value && isSensitiveAttribute(key))
    .map(([key, value]) => ({ key, value }))
}

/** Date ISO/jour → JJ/MM/AAAA FR (null → '—'). */
function formatDate(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR')
}

/** Dates clés présentes sur l'objet, avec libellé + icône. */
function dateChips(asset: OwnedAsset) {
  return (
    [
      { key: 'orderedAt', label: 'Commande', value: asset.orderedAt, icon: ShoppingCartIcon },
      { key: 'expectedAt', label: 'Prévue', value: asset.expectedAt, icon: CalendarDaysIcon },
      { key: 'deliveredAt', label: 'Remise', value: asset.deliveredAt, icon: CheckCircleIcon },
      { key: 'returnedAt', label: 'Restitution', value: asset.returnedAt, icon: InboxArrowDownIcon },
    ] as const
  ).filter((d) => !!d.value)
}

// ── Dialogue de remplacement ──────────────────────────────────────────────────
const replaceDialog = ref<InstanceType<typeof OnbReplacementDialog> | null>(null)
const successNotice = ref('')

function askReplacement(asset: OwnedAsset) {
  const c = collab.value
  if (!c) return
  replaceDialog.value?.open(c.userUuid!, asset)
}

function onReplacementCreated(_ticketId: string) {
  successNotice.value = 'Demande de remplacement créée.'
}

// ── Affectation de matériel (rattrapage du parc) ──────────────────────────────
const assignDialog = ref<InstanceType<typeof OnbAssignAssetDialog> | null>(null)

function openAssign() {
  const c = collab.value
  if (!c) return
  assignDialog.value?.open(c.userUuid!)
}

function onAssigned() {
  successNotice.value = 'Matériel affecté au collaborateur.'
}

// ── Édition d'un équipement (admin) ───────────────────────────────────────────
const editDialog = ref<InstanceType<typeof OnbAssetEditDialog> | null>(null)

function openEdit(asset: AssetAssignment) {
  editDialog.value?.open(asset)
}

function onAssetSaved() {
  successNotice.value = 'Équipement mis à jour.'
}

// Chargement initial (API), PUIS défilement vers le remplacement ciblé par
// l'ancre #repl-<id> (arrivée depuis la liste) une fois le DOM rendu.
onMounted(async () => {
  await store.ensureLoaded()
  if (!route.hash) return
  await nextTick()
  document.querySelector(route.hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
})
</script>

<template>
  <div class="mx-auto w-full max-w-7xl p-4 md:p-8">
    <!-- ── Collaborateur introuvable ─────────────────────────────────────────── -->
    <section
      v-if="!collab"
      class="card border border-base-300 bg-base-100 shadow-sm"
      aria-labelledby="notfound-title"
    >
      <div class="card-body items-center gap-3 py-12 text-center">
        <ExclamationTriangleIcon class="h-12 w-12 text-base-content/50" aria-hidden="true" />
        <h1 id="notfound-title" class="text-xl font-semibold">Collaborateur introuvable</h1>
        <p class="max-w-prose text-base-content/80">
          Aucun collaborateur ne correspond à cette adresse.
        </p>
        <RouterLink to="/onboarding/collaborateurs" class="btn btn-primary">
          Retour aux collaborateurs
        </RouterLink>
      </div>
    </section>

    <template v-else>
      <!-- ── Fil d'Ariane ──────────────────────────────────────────────────── -->
      <nav aria-label="Fil d'Ariane" class="text-sm">
        <ol role="list" class="flex flex-wrap items-center gap-1 text-base-content/70">
          <li><RouterLink to="/accueil" class="link link-hover">Accueil</RouterLink></li>
          <li aria-hidden="true"><ChevronRightIcon class="h-4 w-4" /></li>
          <li>Hub Interne</li>
          <li aria-hidden="true"><ChevronRightIcon class="h-4 w-4" /></li>
          <li><RouterLink to="/onboarding" class="link link-hover">Matériel collaborateur</RouterLink></li>
          <li aria-hidden="true"><ChevronRightIcon class="h-4 w-4" /></li>
          <li>
            <RouterLink to="/onboarding/collaborateurs" class="link link-hover">Collaborateurs</RouterLink>
          </li>
          <li aria-hidden="true"><ChevronRightIcon class="h-4 w-4" /></li>
          <li class="font-medium text-base-content" aria-current="page">{{ fullName() }}</li>
        </ol>
      </nav>

      <!-- ── Confirmation inline (remplacement créé) ───────────────────────── -->
      <div v-if="successNotice" role="status" class="alert alert-success mt-4">
        <CheckCircleIcon class="h-5 w-5 shrink-0" aria-hidden="true" />
        <span>{{ successNotice }}</span>
      </div>

      <!-- ── Carte identité ────────────────────────────────────────────────── -->
      <section
        class="card mt-4 border border-base-300 bg-base-100 shadow-sm"
        aria-labelledby="identity-title"
      >
        <div class="card-body gap-4 p-4 md:p-6">
          <h1 id="identity-title" class="flex items-center gap-2 text-2xl font-bold">
            <UserIcon class="h-6 w-6 text-primary" aria-hidden="true" />
            {{ fullName() }}
          </h1>
          <dl class="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
            <div v-for="f in identityFields" :key="f.label">
              <dt class="text-sm text-base-content/70">{{ f.label }}</dt>
              <dd
                class="text-base break-words text-base-content"
                :class="{ 'tabular-nums': f.numeric }"
              >
                {{ f.value }}
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <!-- ── Remplacements en cours ────────────────────────────────────────── -->
      <section
        v-if="activeTickets.length"
        class="mt-6"
        aria-labelledby="replacements-title"
      >
        <h2
          id="replacements-title"
          class="flex items-center gap-2 text-lg font-semibold"
        >
          <ArrowPathIcon class="h-5 w-5 text-primary" aria-hidden="true" />
          Remplacements en cours
          <span class="badge badge-sm badge-warning tabular-nums">{{ activeTickets.length }}</span>
        </h2>
        <div class="mt-3 grid grid-cols-1 gap-4">
          <div v-for="t in activeTickets" :id="`repl-${t.id}`" :key="t.id" class="scroll-mt-24">
            <OnbReplacementTracker :ticket="t" />
          </div>
        </div>
      </section>

      <!-- ── Matériel détenu ───────────────────────────────────────────────── -->
      <section class="mt-6" aria-labelledby="owned-title">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <h2 id="owned-title" class="flex items-center gap-2 text-lg font-semibold">
            <CubeIcon class="h-5 w-5 text-primary" aria-hidden="true" />
            Matériel détenu
          </h2>
          <button type="button" class="btn btn-primary w-full sm:w-auto" @click="openAssign">
            <PlusIcon class="h-5 w-5" aria-hidden="true" />
            Affecter du matériel
          </button>
        </div>

        <!-- État vide -->
        <div
          v-if="!hasOwned"
          class="card mt-3 border border-base-300 bg-base-100 shadow-sm"
        >
          <div class="card-body items-center gap-3 py-10 text-center">
            <CubeIcon class="h-10 w-10 text-base-content/50" aria-hidden="true" />
            <p class="text-base-content/80">Aucun matériel détenu par ce collaborateur.</p>
          </div>
        </div>

        <template v-else>
          <div v-for="group in ownedGroups" :key="group.category" class="mt-5">
            <h3 class="mb-3 flex items-center gap-2 font-semibold text-base-content/80">
              {{ group.label }}
              <span class="badge badge-sm badge-ghost tabular-nums">{{ group.assets.length }}</span>
            </h3>
            <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <article
                v-for="asset in group.assets"
                :key="asset.id"
                class="card border border-base-300 bg-base-100 shadow-sm"
              >
                <div class="card-body gap-3 p-4">
                  <!-- Titre + statut -->
                  <div class="flex flex-wrap items-start justify-between gap-2">
                    <h4 class="text-base font-semibold break-words">{{ asset.label }}</h4>
                    <OnbAssetStatusBadge :status="asset.status" size="md" />
                  </div>

                  <!-- Infos clés : attributs renseignés selon le type (SN, IMEI, ligne, plaque…) -->
                  <dl
                    v-if="keyAttributes(asset).length"
                    class="grid grid-cols-1 gap-1.5 text-sm sm:grid-cols-2"
                  >
                    <div
                      v-for="attr in keyAttributes(asset)"
                      :key="attr.key"
                      class="flex flex-wrap items-baseline gap-1.5"
                    >
                      <dt class="text-base-content/70">{{ attributeLabel(attr.key) }} :</dt>
                      <dd
                        class="font-medium break-all text-base-content"
                        :class="{ 'tabular-nums': looksNumeric(attr.value) }"
                      >
                        {{ attr.value }}
                      </dd>
                    </div>
                  </dl>

                  <!-- État : fourni à la remise / rendu à la restitution -->
                  <div
                    v-if="asset.condition || (asset.status === 'RETURNED' && asset.returnCondition)"
                    class="flex flex-wrap gap-2"
                  >
                    <span
                      v-if="asset.condition"
                      class="inline-flex items-center gap-1.5 rounded-field bg-base-200 px-2 py-1 text-sm text-base-content/80"
                    >
                      <SparklesIcon class="h-4 w-4 shrink-0" aria-hidden="true" />
                      État fourni :
                      <span class="font-medium text-base-content">{{ CONDITION_LABEL[asset.condition] ?? asset.condition }}</span>
                    </span>
                    <span
                      v-if="asset.status === 'RETURNED' && asset.returnCondition"
                      class="inline-flex items-center gap-1.5 rounded-field bg-base-200 px-2 py-1 text-sm text-base-content/80"
                    >
                      <InboxArrowDownIcon class="h-4 w-4 shrink-0" aria-hidden="true" />
                      État rendu :
                      <span class="font-medium text-base-content">{{ CONDITION_LABEL[asset.returnCondition] ?? asset.returnCondition }}</span>
                    </span>
                  </div>

                  <!-- Dates clés du cycle de vie -->
                  <ul
                    v-if="dateChips(asset).length"
                    role="list"
                    class="flex flex-wrap gap-x-4 gap-y-1 text-sm"
                  >
                    <li
                      v-for="d in dateChips(asset)"
                      :key="d.key"
                      class="inline-flex items-center gap-1.5 text-base-content/80"
                    >
                      <component :is="d.icon" class="h-4 w-4 shrink-0" aria-hidden="true" />
                      <span class="text-base-content/70">{{ d.label }} :</span>
                      <span class="tabular-nums text-base-content">{{ formatDate(d.value) }}</span>
                    </li>
                  </ul>

                  <!-- Attributs sensibles (masqués par défaut) -->
                  <dl
                    v-if="sensitiveAttributes(asset).length"
                    class="grid grid-cols-1 gap-2 text-sm"
                  >
                    <div
                      v-for="attr in sensitiveAttributes(asset)"
                      :key="attr.key"
                      class="flex flex-wrap items-center gap-2"
                    >
                      <dt class="text-base-content/70">{{ attributeLabel(attr.key) }} :</dt>
                      <dd>
                        <OnbSecretValue :value="attr.value" />
                      </dd>
                    </div>
                  </dl>

                  <!-- Actions : remplacement + édition (admin) -->
                  <div class="mt-1 flex flex-wrap items-center gap-2">
                    <p
                      v-if="hasActiveReplacement(asset.id)"
                      class="inline-flex items-center gap-1.5 text-sm text-warning"
                    >
                      <ArrowPathIcon class="h-4 w-4" aria-hidden="true" />
                      Remplacement en cours
                    </p>
                    <button
                      v-else
                      type="button"
                      class="btn btn-ghost"
                      @click="askReplacement(asset)"
                    >
                      <ArrowPathIcon class="h-5 w-5" aria-hidden="true" />
                      Demander un remplacement
                    </button>
                    <button
                      v-if="canManage"
                      type="button"
                      class="btn btn-ghost"
                      @click="openEdit(asset)"
                    >
                      <PencilSquareIcon class="h-5 w-5" aria-hidden="true" />
                      Modifier
                    </button>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </template>
      </section>

      <!-- ── En cours de dotation (lecture seule) ──────────────────────────── -->
      <section
        v-if="collab.pendingAssets.length"
        class="mt-6"
        aria-labelledby="pending-title"
      >
        <h2 id="pending-title" class="text-lg font-semibold">En cours de dotation</h2>
        <div class="mt-3 hidden overflow-x-auto rounded-xl border border-base-300 bg-base-100 shadow-sm md:block">
          <table class="table">
            <thead>
              <tr>
                <th scope="col">Matériel</th>
                <th scope="col" class="hidden md:table-cell">Catégorie</th>
                <th scope="col">Statut</th>
                <th v-if="canManage" scope="col" class="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="asset in collab.pendingAssets" :key="asset.id">
                <td class="font-medium">{{ asset.label }}</td>
                <td class="hidden md:table-cell">{{ CATEGORY_META[asset.category].label }}</td>
                <td><OnbAssetStatusBadge :status="asset.status" size="md" /></td>
                <td v-if="canManage" class="text-right">
                  <button type="button" class="btn btn-ghost btn-sm" @click="openEdit(asset)">
                    <PencilSquareIcon class="h-5 w-5" aria-hidden="true" />
                    Modifier
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- ── Cartes (mobile) ────────────────────────────────────────────── -->
        <ul role="list" class="mt-3 grid grid-cols-1 gap-3 md:hidden">
          <li
            v-for="asset in collab.pendingAssets"
            :key="asset.id"
            class="card border border-base-300 bg-base-100 shadow-sm"
          >
            <div class="card-body gap-1 p-4">
              <div class="flex flex-wrap items-center justify-between gap-2">
                <span class="font-semibold break-words">{{ asset.label }}</span>
                <OnbAssetStatusBadge :status="asset.status" size="md" />
              </div>
              <p class="text-sm text-base-content/80">{{ CATEGORY_META[asset.category].label }}</p>
              <button
                v-if="canManage"
                type="button"
                class="btn btn-ghost btn-block mt-1"
                @click="openEdit(asset)"
              >
                <PencilSquareIcon class="h-5 w-5" aria-hidden="true" />
                Modifier
              </button>
            </div>
          </li>
        </ul>
      </section>

      <!-- ── Historique des remplacements (terminés / annulés) ─────────────── -->
      <details
        v-if="closedTickets.length"
        class="collapse collapse-arrow mt-6 border border-base-300 bg-base-100"
      >
        <summary class="collapse-title font-semibold">
          Historique des remplacements
          <span class="badge badge-sm badge-ghost tabular-nums">{{ closedTickets.length }}</span>
        </summary>
        <div class="collapse-content">
          <div class="grid grid-cols-1 gap-4 pt-2">
            <OnbReplacementTracker v-for="t in closedTickets" :key="t.id" :ticket="t" />
          </div>
        </div>
      </details>

      <!-- ── Modale de demande de remplacement ─────────────────────────────── -->
      <OnbReplacementDialog ref="replaceDialog" @created="onReplacementCreated" />

      <!-- ── Modale d'affectation de matériel (rattrapage) ─────────────────── -->
      <OnbAssignAssetDialog ref="assignDialog" @assigned="onAssigned" />

      <!-- ── Modale d'édition d'un équipement (admin) ──────────────────────── -->
      <OnbAssetEditDialog ref="editDialog" @saved="onAssetSaved" />
    </template>
  </div>
</template>
